// frontend/src/pages/ArticleDetail.js
import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Clock, Eye, Share2, Tag, ArrowRight, BookOpen, Wrench } from 'lucide-react';
import { getArticleBySlug, getRelatedArticles } from '../data/ArticlesData';

// import { AdInline, AdSidebar } from '../components/AdBanner';

// ── Reading progress bar ──────────────────────────────────────
function ReadingProgress({ accentColor }) {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const fn = () => {
      const el = document.documentElement;
      const h  = el.scrollHeight - el.clientHeight;
      setPct(h > 0 ? Math.round((el.scrollTop / h) * 100) : 0);
    };
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: 3, zIndex: 2000, background: 'var(--ink-2)' }}>
      <div style={{ height: '100%', width: `${pct}%`, background: `linear-gradient(90deg, ${accentColor}, var(--red))`, transition: 'width 0.1s', borderRadius: '0 2px 2px 0' }} />
    </div>
  );
}

// ── Build table of contents from HTML ────────────────────────
function buildTOC(html) {
  return [...html.matchAll(/<h2[^>]*>(.*?)<\/h2>/gi)]
    .map((m, i) => ({ id: `h${i}`, label: m[1].replace(/<[^>]+>/g, '') }));
}

function injectIDs(html) {
  let i = 0;
  return html.replace(/<h2([^>]*)>/gi, () => `<h2 id="h${i++}">`);
}

// ── Related article mini card ─────────────────────────────────
function RelatedMini({ article }) {
  const cs = { 'PDF Tools': '#ff6b7a', 'Image Tools': '#60a5fa', 'AI Tools': '#c084fc' };
  
  return (
    <Link to={`/articles/${article.slug}`} style={{ textDecoration: 'none' }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 0', borderBottom: '1px solid var(--border)', transition: 'padding-left 0.2s' }}
        onMouseEnter={e => e.currentTarget.style.paddingLeft = '4px'}
        onMouseLeave={e => e.currentTarget.style.paddingLeft = '0'}>
        <div style={{ width: 48, height: 42, borderRadius: 10, background: article.coverColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
          {article.icon}
        </div>
        <div>
          <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--t-primary)', lineHeight: 1.3, marginBottom: 3 }}>{article.title}</div>
          <div style={{ fontSize: '0.7rem', color: cs[article.category] || 'var(--t-tertiary)' }}>{article.category} · {article.readTime}</div>
        </div>
      </div>
    </Link>
  );
}

// ══════════════════════════════════════════════════════════════
//  ARTICLE DETAIL PAGE
// ══════════════════════════════════════════════════════════════
export default function ArticleDetail() {
  const { slug }     = useParams();
  const navigate     = useNavigate();
  const article      = getArticleBySlug(slug);
  const [activeH, setActiveH] = useState('');

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  useEffect(() => {
    if (!article) return;
    const fn = () => {
      const headings = document.querySelectorAll('.article-body h2');
      let cur = '';
      headings.forEach(h => { if (h.getBoundingClientRect().top < 100) cur = h.id; });
      setActiveH(cur);
    };
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, [article]);

  if (!article) return (
    <div style={{ textAlign: 'center', padding: '120px 20px', color: 'var(--t-secondary)' }}>
      <div style={{ fontSize: 56, marginBottom: 20 }}>📭</div>
      <h2 style={{ fontFamily: 'var(--font-display)', marginBottom: 16, color: 'var(--t-primary)' }}>Article not found</h2>
      <Link to="/articles" className="btn btn-primary">← All Articles</Link>
    </div>
  );

  const toc     = buildTOC(article.content);
  const body    = injectIDs(article.content);
  const related = getRelatedArticles(article, 4);
  const cs      = { 'PDF Tools': '#ff6b7a', 'Image Tools': '#60a5fa', 'AI Tools': '#c084fc' };

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) navigator.share({ title: article.title, url });
    else navigator.clipboard.writeText(url).then(() => alert('Link copied!'));
  };

  return (
    <>
      <ReadingProgress accentColor={article.accentColor} />

   <Helmet>
  <title>{article.title} — ILoveConvert</title>
  <meta name="description" content={article.excerpt} />
  {article.tags && <meta name="keywords" content={article.tags.join(', ')} />}
  <link rel="canonical" href={`https://iloveconvert.in/articles/${article.slug}`} />
  <meta property="og:title" content={article.title} />
  <meta property="og:description" content={article.excerpt} />
  <meta property="og:url" content={`https://iloveconvert.in/articles/${article.slug}`} />
  <meta property="og:type" content="article" />
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content={article.title} />
  <meta name="twitter:description" content={article.excerpt} />
</Helmet>

      {/* ── HERO ────────────────────────────────────────────── */}
      <div style={{ background: article.coverColor, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 80% 60% at 60% 50%, rgba(255,255,255,0.04), transparent 70%)' }} />
        <div style={{ maxWidth: 1240, margin: '0 auto', padding: '56px 28px 44px', position: 'relative', zIndex: 1 }}>
          <Link to="/articles" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, color: 'var(--t-secondary)', textDecoration: 'none', marginBottom: 26, fontSize: '0.88rem', fontWeight: 500 }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--t-primary)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--t-secondary)'}>
            <ArrowLeft size={14} /> All Articles
          </Link>

          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: '0.73rem', fontWeight: 700, padding: '4px 12px', borderRadius: 20, background: 'white', border: `1px solid ${(cs[article.category] || article.accentColor)}40`, color: cs[article.category] || article.accentColor }}>
              {article.category}
            </span>
            {article.tags.slice(0, 3).map(tag => (
              <span key={tag} style={{ fontSize: '0.7rem', padding: '3px 9px', borderRadius: 20, background: 'var(--glass-2)', color: 'var(--t-tertiary)', border: '1px solid var(--border)', fontFamily: 'var(--font-mono)' }}>#{tag}</span>
            ))}
          </div>

          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(1.6rem,4.5vw,2.8rem)', fontWeight: 900, color: 'var(--t-primary)', lineHeight: 1.12, letterSpacing: '-0.025em', marginBottom: 18, maxWidth: 820 }}>
            {article.title}
          </h1>

          <div style={{ display: 'flex', gap: 20, color: 'var(--t-tertiary)', fontSize: '0.82rem', flexWrap: 'wrap', alignItems: 'center' }}>
            {/* <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <Clock size={13} /> {article.readTime}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <Eye size={13} /> {article.views.toLocaleString()} views
            </span>
            <span>{new Date(article.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</span> */}
            <button onClick={handleShare} style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'var(--glass-2)', border: '1px solid var(--border)', borderRadius: 20, padding: '5px 14px', color: 'var(--t-secondary)', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: '0.82rem' }}>
              <Share2 size={12} /> Share
            </button>
          </div>
        </div>
      </div>

      {/* ── CONTENT AREA ────────────────────────────────────── */}
      <div className="article-layout-grid" style={{ maxWidth: 1240, margin: '0 auto', padding: '48px 28px 80px', display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 300px', gap: 52, alignItems: 'start' }}>

        {/* ── Main article ──────────────────────────────────── */}
        <article>
          {/* Excerpt pull-quote */}
          <div style={{ background: 'var(--glass-1)', border: '1px solid var(--border)', borderLeft: `3px solid ${article.accentColor}`, borderRadius: '0 var(--r-md) var(--r-md) 0', padding: '16px 22px', marginBottom: 36, backdropFilter: 'blur(8px)' }}>
            <p style={{ fontSize: '1rem', color: 'var(--t-secondary)', lineHeight: 1.7, fontStyle: 'italic' }}>{article.excerpt}</p>
          </div>

          {/* Try the tool CTA */}
          <Link to={article.toolHref} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 14, background: `linear-gradient(135deg, ${article.accentColor}12, ${article.accentColor}06)`, border: `1px solid ${article.accentColor}30`, borderRadius: 'var(--r-lg)', padding: '14px 20px', marginBottom: 36, transition: 'all 0.2s' }}
            onMouseEnter={e => { e.currentTarget.style.background = `linear-gradient(135deg, ${article.accentColor}20, ${article.accentColor}10)`; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = `linear-gradient(135deg, ${article.accentColor}12, ${article.accentColor}06)`; e.currentTarget.style.transform = ''; }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: article.coverColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>{article.icon}</div>
            <div>
              <div style={{ fontSize: '0.78rem', color: 'var(--t-tertiary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>Try the tool</div>
              <div style={{ fontSize: '0.92rem', fontWeight: 700, color: 'var(--t-primary)' }}>{article.tool} — Free, No Signup</div>
            </div>
            <ArrowRight size={18} style={{ color: article.accentColor, marginLeft: 'auto' }} />
          </Link>

          {/* <AdInline /> */}

          {/* Article body */}
          <div className="article-body" dangerouslySetInnerHTML={{ __html: body }} />

          {/* <AdInline /> */}

          {/* Tags */}
          <div style={{ marginTop: 44, paddingTop: 24, borderTop: '1px solid var(--border)' }}>
            <p style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, marginBottom: 12, color: 'var(--t-primary)', fontSize: '0.88rem' }}>
              <Tag size={14} /> Tags
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {article.tags.map(tag => (
                <span key={tag} style={{ padding: '6px 14px', borderRadius: 50, border: '1px solid var(--border)', background: 'var(--glass-1)', color: 'var(--t-secondary)', fontSize: '0.8rem', fontWeight: 500, fontFamily: 'var(--font-mono)' }}>#{tag}</span>
              ))}
            </div>
          </div>

          {/* Bottom CTA */}
          <div style={{ marginTop: 36, background: `linear-gradient(135deg, ${article.accentColor}12, ${article.accentColor}04)`, border: `1px solid ${article.accentColor}25`, borderRadius: 'var(--r-xl)', padding: '28px 32px', textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>{article.icon}</div>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 700, color: 'var(--t-primary)', marginBottom: 8 }}>Ready to try {article.tool}?</h3>
            <p style={{ color: 'var(--t-secondary)', fontSize: '0.88rem', marginBottom: 20 }}>Free, no account needed, no file size tricks.</p>
            <Link to={article.toolHref} className="btn btn-primary" style={{ justifyContent: 'center', display: 'inline-flex' }}>
              Open {article.tool} →
            </Link>
          </div>
        </article>

        {/* ── SIDEBAR ─────────────────────────────────────── */}
        <aside style={{ position: 'sticky', top: 84, display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* TOC */}
          {toc.length > 0 && (
            <div style={{ background: 'var(--glass-1)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', padding: 20, backdropFilter: 'blur(8px)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '0.88rem', color: 'var(--t-primary)' }}>
                <BookOpen size={14} color={article.accentColor} /> In this article
              </div>
              <nav>
                {toc.map(item => (
                  <a key={item.id} href={`#${item.id}`} style={{ display: 'block', padding: '7px 10px', borderRadius: 8, fontSize: '0.8rem', textDecoration: 'none', marginBottom: 2, lineHeight: 1.4, transition: 'all 0.15s', color: activeH === item.id ? article.accentColor : 'var(--t-secondary)', background: activeH === item.id ? `${article.accentColor}10` : 'transparent', borderLeft: `2px solid ${activeH === item.id ? article.accentColor : 'transparent'}`, fontWeight: activeH === item.id ? 600 : 400 }}>
                    {item.label}
                  </a>
                ))}
              </nav>
            </div>
          )}

          {/* <AdSidebar /> */}

          {/* Related */}
          {related.length > 0 && (
            <div style={{ background: 'var(--glass-1)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', padding: 20, backdropFilter: 'blur(8px)' }}>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '0.88rem', color: 'var(--t-primary)', marginBottom: 4 }}>Related Articles</div>
              {related.map(r => <RelatedMini key={r.id} article={r} />)}
              <Link to="/articles" style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 14, color: 'var(--red)', textDecoration: 'none', fontSize: '0.82rem', fontWeight: 600 }}>
                All articles <ArrowRight size={13} />
              </Link>
            </div>
          )}

          {/* Quick tool links */}
          <div style={{ background: 'var(--glass-1)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', padding: 20, backdropFilter: 'blur(8px)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '0.88rem', color: 'var(--t-primary)', marginBottom: 12 }}>
              <Wrench size={14} color="var(--red)" /> Quick Access
            </div>
            {[
              // PDF to JPG and Compress PDF are hidden while they don't work.
              // ['🖼️ PDF to JPG', '/convert/pdf-to-jpg'],
              // ['🗜️ Compress PDF', '/compress-pdf'],
              ['📑 Merge PDF', '/merge-pdf'],
              ['✂️ Split PDF', '/split-pdf'],
              ['🖼️ Image Convert', '/image-convert'],
            ].map(([label, href]) => (
              <Link key={href} to={href} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', color: 'var(--t-secondary)', textDecoration: 'none', fontSize: '0.85rem', fontWeight: 500, borderBottom: '1px solid var(--border)', transition: 'color 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--t-primary)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--t-secondary)'}>
                {label} <ArrowRight size={12} />
              </Link>
            ))}
          </div>
        </aside>
      </div>

      {/* ── Bottom related grid ──────────────────────────────── */}
      {related.length > 0 && (
        <div style={{ maxWidth: 1240, margin: '0 auto', padding: '0 28px 60px' }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', fontWeight: 700, color: 'var(--t-primary)', marginBottom: 20 }}>More Articles</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
            {related.map(a => (
              <Link key={a.id} to={`/articles/${a.slug}`} style={{ textDecoration: 'none', background: 'var(--glass-1)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', padding: '18px 20px', display: 'flex', gap: 14, alignItems: 'flex-start', transition: 'all 0.2s', backdropFilter: 'blur(8px)' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = a.accentColor + '44'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = ''; e.currentTarget.style.transform = ''; }}>
                <div style={{ width: 44, height: 40, borderRadius: 10, background: a.coverColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>{a.icon}</div>
                <div>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, color: cs[a.category] || 'var(--t-tertiary)', display: 'block', marginBottom: 4 }}>{a.category}</span>
                  <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--t-primary)', lineHeight: 1.3 }}>{a.title}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Article body styles */}
      <style>{`
        .article-body h2 { font-family:var(--font-display); font-size:1.45rem; font-weight:800; color:var(--t-primary); margin:2.4rem 0 1rem; letter-spacing:-0.02em; scroll-margin-top:90px; }
        .article-body h3 { font-family:var(--font-display); font-size:1.1rem; font-weight:700; color:var(--t-primary); margin:1.8rem 0 0.7rem; }
        .article-body p  { margin-bottom:1.2rem; line-height:1.8; font-size:0.96rem; color:var(--t-secondary); }
        .article-body ul, .article-body ol { margin:0.8rem 0 1.4rem 1.6rem; }
        .article-body li { margin-bottom:0.5rem; line-height:1.7; font-size:0.94rem; color:var(--t-secondary); }
        .article-body strong { color:var(--t-primary); font-weight:600; }
        .article-body a { color:var(--red); text-decoration:underline; text-underline-offset:3px; }
        .article-body code { background:var(--ink-2); padding:2px 7px; border-radius:5px; font-size:0.87em; color:var(--red); font-family:var(--font-mono); }
        .article-body pre { background:var(--ink-2); border:1px solid var(--border); color:var(--t-primary); padding:20px; border-radius:var(--r-md); overflow-x:auto; margin:1.5rem 0; font-size:0.87rem; font-family:var(--font-mono); line-height:1.65; }
        .article-body pre code { background:none; color:var(--t-primary); padding:0; }
        .article-body blockquote { border-left:3px solid var(--red); padding:14px 22px; background:rgba(255,45,75,0.04); border-radius:0 var(--r-sm) var(--r-sm) 0; margin:1.5rem 0; font-style:italic; }
        .article-body table { width:100%; border-collapse:collapse; margin:1.5rem 0; font-size:0.88rem; }
        .article-body th { background:var(--ink-2); font-weight:700; color:var(--t-primary); padding:10px 14px; text-align:left; border:1px solid var(--border-mid); }
        .article-body td { padding:10px 14px; border:1px solid var(--border); color:var(--t-secondary); }
        .article-body tbody tr:hover td { background:var(--glass-1); color:var(--t-primary); }
        @media(max-width:768px){ article + aside { display:none; } }
      `}</style>
    </>
  );
}
